import { useEffect, useState } from 'react';
import { ApiCallError, call } from '@/api.ts';
import type { Idp, IdpFinding, IdpProvider, SessionRow } from '@/api.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Input, Label } from '@/components/ui/input.tsx';
import { Segmented } from '@/components/ui/segmented.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';
import {
  checkGroupRows, mapFromRows, ROLE_OPTIONS, rowsFromMap, warnsAboutLockout,
} from '@/lib/group-map.ts';
import type { GroupRow } from '@/lib/group-map.ts';
import { connectionLine, ROLE_REACH, testSummary, worstSeverity } from '@/lib/idp-status.ts';
import { refusal, roleRefusal } from '@/lib/refusal.ts';
import { canAdminister, useAuthenticated } from '@/session.tsx';
import type { Role } from '@/session.tsx';

/**
 * The Identity page: connect the tenant's directory, and map its groups onto Braid roles.
 *
 * Two columns, because they are two different questions and operators arrive with one of them.
 * Left is the connection — a protocol, a discovery document, and a secret that goes one way.
 * Right is who that connection lets in, which is the piece with no OSS component behind it,
 * because no two directories spell their groups the same way.
 *
 * Sessions sit below both. The handoff does not draw them — it draws the two questions above —
 * but revoking a leaked session is shipped behaviour and deleting a working control to match a
 * mock would be the wrong trade. Full width, under the fold, where it does not compete.
 */
export function IdentityPage() {
  const session = useAuthenticated();
  const administers = canAdminister(session.role);

  return (
    <div className="grid gap-14">
      <section className="grid gap-8 lg:grid-cols-2 lg:gap-14">
        <ConnectionColumn canEdit={administers} />
      </section>
      <SessionsSection canEdit={administers} />
    </div>
  );
}

/**
 * The connection form and the group map, as one component.
 *
 * They look like two columns and they are one form: the group map is sent in the same `PUT /idp`
 * body as the client id, so splitting them into sibling components would mean lifting every field
 * into a parent anyway and gaining a boundary that owns nothing.
 */
function ConnectionColumn({ canEdit }: { canEdit: boolean }) {
  const session = useAuthenticated();
  const [idp, setIdp] = useState<Idp | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [findings, setFindings] = useState<IdpFinding[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const [provider, setProvider] = useState<IdpProvider>('oidc');
  const [metadataUrl, setMetadataUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [rows, setRows] = useState<GroupRow[]>([{ group: '', role: 'viewer' }]);

  useEffect(() => {
    let live = true;
    call<{ idp: Idp }>(session, '/idp')
      .then((result) => {
        if (!live) return;
        setIdp(result.idp);
        setProvider(result.idp.provider);
        setMetadataUrl(result.idp.metadataUrl ?? '');
        setClientId(result.idp.clientId);
        setRows([...rowsFromMap(result.idp.groupMap), { group: '', role: 'viewer' }]);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        // 404 is the ordinary "not configured yet" answer, not a failure.
        if (cause instanceof ApiCallError && cause.status === 404) setIdp(null);
        else setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => { live = false; };
  }, [session]);

  const problems = checkGroupRows(rows);
  const lockout = warnsAboutLockout(rows);

  function body() {
    return {
      provider,
      ...(metadataUrl.trim() ? { metadataUrl: metadataUrl.trim() } : {}),
      ...(clientId.trim() ? { clientId: clientId.trim() } : {}),
      // Only sent when the operator typed one. An empty field means "leave the stored secret
      // alone", not "clear it" — the API never returns it, so a blank box is the normal state.
      ...(secret ? { secret } : {}),
      groupMap: mapFromRows(rows),
    };
  }

  async function test() {
    setBusy(true);
    setStatus(null);
    // Timed here rather than reported by the route, because this is the clock that measured it.
    const started = performance.now();
    try {
      const result = await call<{ ok: boolean; findings: IdpFinding[] }>(session, '/idp/test', {
        method: 'POST',
        body: JSON.stringify(body()),
      });
      setFindings(result.findings);
      setStatus({
        tone: result.ok ? 'ok' : 'error',
        text: testSummary(result.ok, performance.now() - started, result.findings),
      });
    } catch (cause) {
      setStatus({ tone: 'error', text: describe(cause) });
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setStatus(null);
    try {
      const result = await call<{ idp: Idp; findings: IdpFinding[] }>(session, '/idp', {
        method: 'PUT',
        body: JSON.stringify(body()),
      });
      setIdp(result.idp);
      setFindings(result.findings);
      // Cleared on success: it has gone to Secrets Manager, and holding it in a form field for
      // the rest of the session is the same mistake as storing it.
      setSecret('');
      setStatus({ tone: 'ok', text: 'Saved. New sign-ins use this configuration immediately.' });
    } catch (cause) {
      const findingsFromError = cause instanceof ApiCallError && Array.isArray(cause.details)
        ? (cause.details as IdpFinding[])
        : [];
      setFindings(findingsFromError);
      setStatus({ tone: 'error', text: describe(cause) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await call(session, '/idp', { method: 'DELETE' });
      setConfirmRemove(false);
      setIdp(null);
      setRows([{ group: '', role: 'viewer' }]);
      setStatus({ tone: 'ok', text: 'Federation removed. Everyone signed in through it has been signed out.' });
    } catch (cause) {
      setStatus({ tone: 'error', text: describe(cause) });
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the identity provider</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (idp === undefined) {
    return (
      <div className="grid content-start gap-4">
        <Skeleton className="h-9 w-56" />
        {[0, 1, 2, 3].map((row) => <Skeleton key={row} className="h-4 w-full" />)}
      </div>
    );
  }

  const line = connectionLine(idp);
  const severity = worstSeverity(findings);
  const cannotEdit = roleRefusal(session.role, 'change federation', canEdit);

  return (
    <>
      {/* Left: the connection. */}
      <div className="grid min-w-0 content-start gap-5">
        <header className="grid gap-2">
          <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">Identity</h1>
          <p className="max-w-[60ch] text-sm leading-relaxed">
            Federate this organization to your own directory. Braid never holds anyone's password —
            your provider authenticates them, and the group claims it returns decide what they
            reach.
          </p>
        </header>

        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-y border-[color:var(--color-border)] py-2.5">
          <Badge variant={line.tone === 'error' ? 'destructive' : line.tone === 'connected' ? 'success' : 'secondary'}>
            {line.label}
          </Badge>
          <span className="text-muted-foreground font-mono text-[12px]">{line.detail}</span>
        </div>

        {cannotEdit ? (
          <Alert>
            <AlertDescription>{cannotEdit}</AlertDescription>
          </Alert>
        ) : null}

        {status ? (
          // The success panel: a cyan left rule rather than a card, so it reads as an annotation
          // on the form above it rather than as a second thing to act on.
          <div
            className={
              status.tone === 'ok'
                ? 'border-l-[3px] border-[color:var(--color-primary)] bg-[color:var(--color-accent)] px-3.5 py-3'
                : 'border-l-[3px] border-[color:var(--color-destructive)] px-3.5 py-3'
            }
          >
            <p className="text-[13.5px] leading-relaxed">{status.text}</p>
          </div>
        ) : null}

        {severity ? (
          <Alert variant={severity === 'error' ? 'destructive' : 'warning'}>
            <AlertTitle>{severity === 'error' ? 'This configuration cannot work' : 'Worth checking'}</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 grid gap-1">
                {findings.map((finding) => (
                  <li key={finding.code}>
                    <code className="font-mono text-xs">{finding.code}</code> — {finding.message}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}

        <fieldset className="grid gap-4" disabled={!canEdit}>
          <div className="grid gap-1.5">
            <Label htmlFor="idp-provider-oidc">Protocol</Label>
            <Segmented
              name="idp-provider"
              value={provider}
              disabled={!canEdit}
              options={[{ value: 'oidc', label: 'OIDC' }, { value: 'saml', label: 'SAML' }]}
              onChange={(value) => setProvider(value as IdpProvider)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="idp-metadata">
              {provider === 'saml' ? 'Metadata URL' : 'Discovery URL'}
            </Label>
            <Input
              id="idp-metadata"
              value={metadataUrl}
              placeholder="https://idp.example.com/.well-known/openid-configuration"
              className="font-mono text-xs"
              onChange={(event) => setMetadataUrl(event.target.value)}
            />
            <p className="text-muted-foreground text-[12.5px] leading-relaxed">
              Must be https — this document declares the keys that decide who is who.
            </p>
          </div>

          {provider === 'oidc' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid content-start gap-1.5">
                <Label htmlFor="idp-client">Client id</Label>
                <Input
                  id="idp-client"
                  value={clientId}
                  className="font-mono text-xs"
                  onChange={(event) => setClientId(event.target.value)}
                />
              </div>
              <div className="grid content-start gap-1.5">
                <Label htmlFor="idp-secret">Client secret</Label>
                <Input
                  id="idp-secret"
                  type="password"
                  value={secret}
                  placeholder={idp?.secretArn ? '•••••••• (stored)' : ''}
                  autoComplete="off"
                  onChange={(event) => setSecret(event.target.value)}
                />
              </div>
              <p className="text-muted-foreground text-[12.5px] leading-relaxed sm:col-span-2">
                {idp?.secretArn
                  ? 'A secret is stored. Leave this blank to keep it; type a new one to replace it. '
                  : 'It goes to Secrets Manager on save. '}
                The secret never appears in a response, on the record this page reads, or in an
                audit entry — there is no code path anywhere that can show it to you again.
              </p>
            </div>
          ) : null}
        </fieldset>

        <div className="flex flex-wrap items-center gap-2 border-t border-[color:var(--color-border)] pt-4">
          <Button
            onClick={save}
            disabled={busy || problems.length > 0 || !canEdit}
            title={refusal(
              cannotEdit,
              problems.length > 0 ? 'The group map has a problem to fix first.' : null,
            )}
          >
            {idp ? 'Save connection' : 'Enable federation'}
          </Button>
          <Button variant="outline" onClick={test} disabled={busy || !canEdit} title={refusal(cannotEdit)}>
            Test sign-in
          </Button>
          {idp ? (
            <Button
              variant="ghost"
              className="ml-auto"
              disabled={busy || !canEdit}
              title={refusal(cannotEdit)}
              onClick={() => setConfirmRemove(true)}
            >
              Remove federation
            </Button>
          ) : null}
        </div>

        <ConfirmDialog
          open={confirmRemove}
          onOpenChange={setConfirmRemove}
          title="Remove federation?"
          description={
            'Everyone signed in through this provider is signed out immediately, and the user pool '
            + 'is deleted. Reconfiguring later creates a new one, so the pool id below will change.'
          }
          confirmLabel="Remove federation"
          destructive
          busy={busy}
          onConfirm={() => { void remove(); }}
        />
      </div>

      {/* Right: who the connection lets in. */}
      <div className="grid min-w-0 content-start gap-6">
        <GroupMap
          rows={rows}
          canEdit={canEdit}
          refusalReason={cannotEdit}
          problems={problems}
          lockout={lockout}
          onChange={setRows}
        />

        <div className="grid gap-3 border-t border-[color:var(--color-border)] pt-6">
          <h2 className="text-[11px] font-medium tracking-[0.12em] uppercase">What each role reaches</h2>
          <dl className="grid gap-2.5">
            {ROLE_REACH.map((entry) => (
              <div key={entry.role} className="grid gap-1 sm:grid-cols-[110px_1fr] sm:gap-3">
                <dt className="font-mono text-[13px] font-semibold">{entry.role}</dt>
                <dd className="text-[13px] leading-relaxed">{entry.reaches}</dd>
              </div>
            ))}
          </dl>
          <p className="text-muted-foreground text-[12.5px] leading-relaxed">
            A signed-in visitor carries an httpOnly session cookie, not a token any script on the
            page can read, and the assertion behind it is signed by your provider — so a fragment
            cannot mint a role for itself by writing one into the document.
          </p>
        </div>
      </div>
    </>
  );
}

function GroupMap({
  rows, canEdit, refusalReason, problems, lockout, onChange,
}: {
  rows: GroupRow[];
  canEdit: boolean;
  refusalReason: string | null;
  problems: { index: number; message: string }[];
  lockout: boolean;
  onChange(rows: GroupRow[]): void;
}) {
  const problemFor = (index: number) => problems.find((problem) => problem.index === index)?.message;

  return (
    <div className="grid gap-3">
      <div className="grid gap-1">
        <h2 className="text-[25px] leading-tight font-semibold tracking-[-0.01em]">Group map</h2>
        <p className="text-[13px] leading-relaxed">
          A group not listed here grants nothing. Someone in several listed groups gets the most
          privileged of them.
        </p>
      </div>

      {lockout ? (
        <Alert variant="warning">
          <AlertTitle>Nobody would be able to administer this organization</AlertTitle>
          <AlertDescription>
            No group grants owner or admin, so nobody signing in through your directory could
            change this page. Undoing it would need an API token.
          </AlertDescription>
        </Alert>
      ) : null}

      <Table>
        <colgroup>
          <col />
          <col className="w-[130px]" />
          <col className="w-[56px]" />
        </colgroup>
        <TableHeader className="[&_tr]:border-foreground [&_tr]:border-b">
          <TableRow>
            <TableHead>IdP group</TableHead>
            <TableHead>Braid role</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              <TableCell className="align-top">
                <Input
                  aria-label={`Directory group ${index + 1}`}
                  value={row.group}
                  disabled={!canEdit}
                  placeholder="finance-team"
                  className="font-mono text-[13px]"
                  aria-invalid={Boolean(problemFor(index))}
                  title={refusal(refusalReason)}
                  onChange={(event) => onChange(
                    rows.map((entry, position) =>
                      (position === index ? { ...entry, group: event.target.value } : entry)),
                  )}
                />
                {problemFor(index) ? (
                  <p className="text-destructive mt-1 text-xs">{problemFor(index)}</p>
                ) : null}
              </TableCell>

              <TableCell className="align-top">
                <select
                  aria-label={`Role for group ${index + 1}`}
                  value={row.role}
                  disabled={!canEdit}
                  title={refusal(refusalReason)}
                  onChange={(event) => onChange(
                    rows.map((entry, position) =>
                      (position === index ? { ...entry, role: event.target.value as Role } : entry)),
                  )}
                  className="border-input bg-background h-9 w-full rounded-[var(--radius)] border px-2 text-[13px]"
                >
                  {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
                </select>
              </TableCell>

              <TableCell className="text-right align-top">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!canEdit}
                  title={refusal(refusalReason)}
                  aria-label={`Remove group ${index + 1}`}
                  onClick={() => onChange(rows.filter((_, position) => position !== index))}
                >
                  Remove
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canEdit}
          title={refusal(refusalReason)}
          onClick={() => onChange([...rows, { group: '', role: 'viewer' }])}
        >
          Add mapping
        </Button>
      </div>
    </div>
  );
}

function SessionsSection({ canEdit }: { canEdit: boolean }) {
  const session = useAuthenticated();
  const [items, setItems] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => {
    let live = true;
    call<{ items: SessionRow[] }>(session, '/sessions')
      .then((result) => { if (live) setItems(result.items); })
      .catch((cause: unknown) => { if (live) setError(describe(cause)); });
    return () => { live = false; };
  }, [session, nonce]);

  async function revoke(path: string) {
    setBusy(true);
    try {
      await call(session, path, { method: 'DELETE' });
      setNonce((value) => value + 1);
    } catch (cause) {
      setError(describe(cause));
    } finally {
      setBusy(false);
    }
  }

  const live = items?.filter((item) => !item.revokedAt) ?? [];
  const cannotEdit = roleRefusal(session.role, 'end sessions', canEdit);

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[color:var(--color-border)] pb-2.5">
        <div className="grid gap-1">
          <h2 className="text-[25px] leading-tight font-semibold tracking-[-0.01em]">Sessions</h2>
          <p className="max-w-[70ch] text-[13px] leading-relaxed">
            Everyone currently signed in through your directory. Revoking takes effect within
            seconds rather than instantly — see the revocation window in the runbook.
          </p>
        </div>
        {live.length > 0 ? (
          <Button
            variant="outline"
            size="sm"
            disabled={busy || !canEdit}
            title={refusal(cannotEdit)}
            onClick={() => setConfirmAll(true)}
          >
            Sign out everyone
          </Button>
        ) : null}
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load sessions</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {!items ? (
        <div className="grid gap-3">
          {[0, 1].map((row) => <Skeleton key={row} className="h-4 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nobody is signed in"
          description="Sessions appear here once someone signs in through your directory."
        />
      ) : (
        <Table>
          <colgroup>
            <col />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[140px]" />
            <col className="w-[110px]" />
          </colgroup>
          <TableHeader className="[&_tr]:border-foreground [&_tr]:border-b">
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Signed in</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>From</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.sessionId} className={item.revokedAt ? 'opacity-50' : undefined}>
                <TableCell className="text-[13px]">
                  {item.email ?? item.subject}
                  {item.revokedAt ? (
                    <span className="ml-2 align-middle"><Badge variant="secondary">revoked</Badge></span>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">
                  {new Date(item.createdAt).toLocaleString()}
                </TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">
                  {new Date(item.expiresAt).toLocaleString()}
                </TableCell>
                <TableCell className="text-muted-foreground max-w-48 truncate font-mono text-xs" title={item.userAgent}>
                  {item.ip ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  {!item.revokedAt ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy || !canEdit}
                      title={refusal(cannotEdit)}
                      onClick={() => revoke(`/sessions/${item.sessionId}`)}
                    >
                      Sign out
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title={`Sign out all ${live.length} sessions?`}
        description="Everyone signed in has to authenticate again. Use this if you think a session has leaked."
        confirmLabel="Sign out everyone"
        destructive
        busy={busy}
        onConfirm={() => { void revoke('/sessions').then(() => setConfirmAll(false)); }}
      />
    </section>
  );
}

function describe(cause: unknown): string {
  return cause instanceof ApiCallError ? `${cause.code}: ${cause.message}` : String(cause);
}
